import { MessageOutlined } from '@ant-design/icons';
import { Badge, Space, Typography, theme } from 'antd';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useChatMessages from '../../hooks/plugins/chat/useChatMessages';
import useChatSessionConnectionState from '../../hooks/plugins/chat/useChatSessionConnectionState';
import useTicketByKey from '../../hooks/ticketDetailPage/useTicketByKey';
import { TicketTab, status, statusOptions } from '../../model/types';
import { getSelectedTabKeyFromUrl } from '../../store/childStore/tabStore';
import { TAB_BAR_HEIGHT, tagBackgroundColors } from '../../styles/theme';
import { getTabStore } from '../../utils/store/getStore';
import { getTicketKeyNumberPart } from '../../utils/ticketHelper';
import MiniTag from '../MiniTag';
import PopoverTicketDetail from '../PopoverTicketDetail';
import WatchingTicketActivityIndicator from '../TicketActivityIndicator/components/WatchingTicketActivityIndicator';
import TicketActivityListener from '../TicketActivityListener';
import { chatAppStateColorMap } from '../plugins/chat/types';

const { Text } = Typography;

interface Props {
    tab: TicketTab;
    dispatchChatSessionMessages?: (...args: any[]) => void;
    dispatchChatSessionState?: (...args: any[]) => void;
    dispatchChatSessionId?: (...args: any[]) => void;
}

interface SelectedTabIndicatorProps {
    backgroundColor: (typeof tagBackgroundColors)[status];
    isSelected: boolean;
}

function SelectedTabIndicator({ backgroundColor, isSelected }: SelectedTabIndicatorProps) {
    return (
        <div
            className={clsx(isSelected && 'full-height')}
            style={{
                backgroundColor,
                width: '5px',
                display: isSelected ? 'block' : 'hidden',
                position: 'absolute',
                top: 0,
                // https://github.com/ant-design/ant-design/blob/master/components/tabs/style/index.ts#L877
                left: 0,
                maxHeight: TAB_BAR_HEIGHT
            }}
        />
    );
}

const styles = {
    icon: { fontSize: '1.6rem' },
    activeIcon: { fontSize: '1.6rem', color: chatAppStateColorMap.ONLINE }
};

export default function TicketTabComponent({ tab }: Props) {
    const {
        token: { fontSizeSM }
    } = theme.useToken();
    const {
        channel,
        description,
        key,
        requester,
        status: { name: status },
        subject
    } = tab;
    const realDescription = description?.replaceAll('<p', '<p style="margin: 0" ');

    const { asPath } = useRouter();

    // to get up-to-date ticket information
    const { data } = useTicketByKey(tab.key, { revalidateOnMount: true });

    const selectedTabKey = getSelectedTabKeyFromUrl(asPath);
    const isCurrentTab = selectedTabKey === key;
    useEffect(() => {
        isCurrentTab && data && getTabStore().updateTicketTabs(data);
    }, [data, isCurrentTab]);

    const hasPopover = !isCurrentTab && !key?.startsWith('newTicket');

    const { t } = useTranslation('common');

    const isChatTicket = channel === 'CHAT';
    const showMessageBadge =
        isChatTicket && !isCurrentTab && tab && 'unreadMessagesCount' in tab && tab.unreadMessagesCount > 0;

    const chatSessionId = isChatTicket ? tab.chatSessionId : null;

    const audioEl = useRef<HTMLAudioElement>(null);
    useChatSessionConnectionState(chatSessionId, key, status === statusOptions.Closed);
    useChatMessages(chatSessionId, key, audioEl);

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <audio ref={audioEl} src="https://bigsoundbank.com/UPLOAD/ogg/2038.ogg" preload="auto" />
            <TicketActivityListener tabKey={tab.key} />

            <PopoverTicketDetail
                ticketKey={tab.key}
                subject={subject}
                ticketStatus={status}
                requester={requester}
                description={description}
                isVisible={hasPopover}
            >
                <Space>
                    <SelectedTabIndicator
                        backgroundColor={tagBackgroundColors[status]}
                        isSelected={selectedTabKey === key}
                    />
                    {isChatTicket ? (
                        <MessageOutlined style={isCurrentTab ? styles.activeIcon : styles.icon} />
                    ) : (
                        <MiniTag status={status} />
                    )}

                    <Space direction="vertical" size={0}>
                        <Text strong ellipsis style={{ minWidth: 100, maxWidth: 100 }}>
                            {t(subject as any)}
                        </Text>

                        {key && (
                            <Space>
                                {isChatTicket ? (
                                    <Text
                                        ellipsis
                                        style={{
                                            fontSize: fontSizeSM,
                                            minWidth: 100,
                                            maxWidth: 100
                                        }}
                                    >
                                        <span dangerouslySetInnerHTML={{ __html: realDescription }}></span>
                                    </Text>
                                ) : !key.startsWith('newTicket') ? (
                                    <Text code style={{ fontSize: fontSizeSM }}>
                                        {`${t('ticket')} ${getTicketKeyNumberPart(data)}`}
                                    </Text>
                                ) : null}

                                {/* Only render for the current tab */}
                            </Space>
                        )}
                    </Space>

                    {!key.startsWith('newTicket') && (
                        <WatchingTicketActivityIndicator key={key} showable={!hasPopover} ticketKey={key} />
                    )}
                </Space>
                {showMessageBadge && <Badge count={tab.unreadMessagesCount} offset={[0, 5]} overflowCount={10} />}
            </PopoverTicketDetail>
        </div>
    );
}
